import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { C } from '../constants';
import Glass from '../components/Glass';
import { supabase } from '../supabase';
import { setMeta, setJsonLd, removeJsonLd } from '../utils/meta';

const CATEGORY_COLORS = {
  analysis: { bg: 'rgba(0,229,255,0.12)', text: C.cyan,  label: 'Analysis' },
  guide:    { bg: 'rgba(120,90,255,0.12)', text: '#A78BFA', label: 'Guide' },
  story:    { bg: 'rgba(255,77,158,0.12)', text: C.pink,  label: 'Story' },
};

function BlogCard({ post, accent }) {
  const navigate = useNavigate();
  const cat = CATEGORY_COLORS[post.category] || CATEGORY_COLORS.guide;

  return (
    <Glass
      onClick={() => navigate(`/blog/${post.slug}`)}
      style={{
        padding: '1.5rem',
        cursor: 'pointer',
        transition: 'border-color 200ms ease, box-shadow 200ms ease',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = accent;
        e.currentTarget.style.boxShadow = `0 0 24px ${accent}22`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = C.border;
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Category + reading time */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span style={{
          fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em',
          textTransform: 'uppercase', padding: '3px 10px', borderRadius: 999,
          background: cat.bg, color: cat.text,
        }}>
          {cat.label}
        </span>
        <span style={{ fontSize: '0.7rem', color: C.textDim }}>
          {post.reading_mins} min read
        </span>
      </div>

      {/* Title */}
      <h3 style={{
        fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.3,
        color: C.white, letterSpacing: '-0.01em', margin: 0,
      }}>
        {post.title}
      </h3>

      {/* Excerpt */}
      <p style={{
        fontSize: '0.85rem', color: C.textDim, lineHeight: 1.55,
        margin: 0, flexGrow: 1,
      }}>
        {post.excerpt}
      </p>

      {/* Tags + date */}
      <div className="flex items-center justify-between gap-2 flex-wrap" style={{ marginTop: 'auto' }}>
        <div className="flex gap-1.5 flex-wrap">
          {post.tags.slice(0, 3).map(t => (
            <span key={t} style={{
              fontSize: '0.62rem', padding: '2px 8px', borderRadius: 999,
              background: 'rgba(255,255,255,0.05)', color: C.textDim,
              border: `1px solid ${C.border}`,
            }}>
              {t}
            </span>
          ))}
        </div>
        <span style={{ fontSize: '0.68rem', color: C.textDimmer }}>
          {new Date(post.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
        </span>
      </div>
    </Glass>
  );
}

export default function BlogListScreen() {
  const [posts, setPosts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const accent = C.cyan;

  useEffect(() => {
    setMeta({
      title:       'Blog - Election Analysis, Guides & Stories | UnofficialElections',
      description: 'In-depth analysis of Indian state elections, how-to guides on reading election data, and stories from the platform. Covering TN, WB, and the seven 2027 states.',
      ogImage:     '/og-home.png',
      canonical:   '/blog',
    });
  }, []);

  useEffect(() => {
    supabase
      .from('blogs')
      .select('slug, title, category, tags, excerpt, reading_mins, published_at')
      .order('published_at', { ascending: false })
      .then(({ data }) => {
        const rows = data || [];
        setPosts(rows);
        setLoading(false);

        // ItemList structured data — one entry per post
        setJsonLd('blog-list', {
          '@context': 'https://schema.org',
          '@type':    'ItemList',
          name:       'UnofficialElections Blog',
          url:        'https://unofficialelections.com/blog',
          itemListElement: rows.map((p, i) => ({
            '@type':  'ListItem',
            position: i + 1,
            url:      `https://unofficialelections.com/blog/${p.slug}`,
            name:     p.title,
          })),
        });
      });

    return () => removeJsonLd('blog-list');
  }, []);

  const categories = ['all', 'analysis', 'guide', 'story'];
  const filtered = filter === 'all' ? posts : posts.filter(p => p.category === filter);

  return (
    <main className="px-5 md:px-10 py-10 max-w-6xl mx-auto">
      {/* Header */}
      <div style={{ fontSize: '0.7rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: accent, fontWeight: 600 }}>
        Blog · Articles & Analysis
      </div>
      <h1 style={{
        fontWeight: 700, fontSize: 'clamp(2rem, 5vw, 3.2rem)',
        lineHeight: 1.05, letterSpacing: '-0.025em',
        marginTop: '0.4rem', color: C.white,
      }}>
        Insights on Indian Elections,
        <br />
        <span style={{
          background: `linear-gradient(90deg, ${accent}, ${C.pink})`,
          WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
          fontStyle: 'italic',
        }}>
          district by district.
        </span>
      </h1>

      {/* Filter pills */}
      <div className="flex gap-2 flex-wrap mt-6 mb-8">
        {categories.map(cat => {
          const active = filter === cat;
          const c = cat === 'all' ? null : CATEGORY_COLORS[cat];
          return (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              style={{
                padding: '6px 16px', borderRadius: 999, fontSize: '0.78rem',
                fontWeight: 600, cursor: 'pointer', fontFamily: "'Poppins', sans-serif",
                border: `1px solid ${active ? (c?.text || accent) : C.border}`,
                background: active ? (c?.bg || `rgba(0,229,255,0.12)`) : 'transparent',
                color: active ? (c?.text || accent) : C.textDim,
                transition: 'all 150ms ease',
              }}
            >
              {cat === 'all' ? 'All posts' : CATEGORY_COLORS[cat].label}
            </button>
          );
        })}
      </div>

      {/* Grid */}
      {loading ? (
        <div style={{ color: C.textDim, fontSize: '0.95rem', textAlign: 'center', padding: '4rem 0' }}>
          Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map(post => (
            <BlogCard key={post.slug} post={post} accent={accent} />
          ))}
        </div>
      )}
    </main>
  );
}
